import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Grid, TextField, Typography, Avatar,
  InputAdornment, Divider, Autocomplete as MuiAutocomplete, CircularProgress, Link, Fade, MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";
import MarkunreadMailboxOutlinedIcon from "@mui/icons-material/MarkunreadMailboxOutlined";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import { useCreateCustomerMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { useDuplicatePhoneCheck } from "../useDuplicatePhoneCheck";
import { useLeadDuplicateCheck } from "../../LeadSystem/useLeadDuplicateCheck";
import DuplicateClientCard from "../../LeadSystem/DuplicateClientCard";
import { validateCustomerEmail, validateCustomerPhone } from "../fieldValidation";
import { preferredCallTimes } from "../constants";
import { toErrorMessage } from "../errorMessage";

/* ── Google Places script loader (singleton promise) ─────────────────── */
let _placesPromise: Promise<void> | null = null;

function loadPlacesScript(key: string): Promise<void> {
  if (_placesPromise) return _placesPromise;
  // Already loaded by a previous call or an external script tag
  if (typeof google !== "undefined" && google.maps?.places) {
    console.log("Google Maps Places: already available on window.google");
    return (_placesPromise = Promise.resolve());
  }
  _placesPromise = new Promise<void>((resolve, reject) => {
    // Use the `callback` param — Google calls it only after ALL requested
    // libraries (including `places`) are fully initialised. This is more
    // reliable than `script.onload`, which can fire before the library
    // objects are attached to `google.maps`.
    const cbName = `__googleMapsPlacesCb_${Date.now()}`;
    (window as any)[cbName] = () => {
      delete (window as any)[cbName];
      console.log("Google Maps Places: callback fired — API ready");
      console.log("Google loaded:", !!window.google);
      resolve();
    };
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&callback=${cbName}`;
    s.async = true;
    s.onerror = () => {
      _placesPromise = null; // allow retry on next mount
      reject(new Error("Google Maps script failed to load"));
    };
    document.head.appendChild(s);
  });
  return _placesPromise;
}

/* ── Parsed address shape ────────────────────────────────────────────── */
interface ParsedAddress {
  streetNumber: string;
  streetName:   string;
  city:         string;
  province:     string;
  postalCode:   string;
}

/* ── Address autocomplete field ──────────────────────────────────────── */
type Prediction = google.maps.places.AutocompletePrediction;

function AddressAutocomplete({ onSelect, inputSx: sx }: {
  onSelect: (a: ParsedAddress) => void;
  inputSx:  object;
}) {
  const [inputValue, setInputValue]   = useState("");
  const [options, setOptions]         = useState<Prediction[]>([]);
  const [open, setOpen]               = useState(false);
  const [loading, setLoading]         = useState(false);
  const [apiReady, setApiReady]       = useState(false);

  const onSelectRef  = useRef(onSelect);
  const svcRef       = useRef<google.maps.places.AutocompleteService | null>(null);
  const detailsRef   = useRef<google.maps.places.PlacesService | null>(null);
  const mapNodeRef   = useRef<HTMLDivElement | null>(null);
  const debounceRef  = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep callback ref fresh every render
  useEffect(() => { onSelectRef.current = onSelect; });

  // Load script once and initialise both services
  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_PLACES_API_KEY ?? "";
    if (!key) return;
    loadPlacesScript(key)
      .then(() => {
        svcRef.current = new google.maps.places.AutocompleteService();
        if (!mapNodeRef.current) mapNodeRef.current = document.createElement("div");
        detailsRef.current = new google.maps.places.PlacesService(mapNodeRef.current);
        setApiReady(true);
      })
      .catch(() => { /* API unavailable — falls back to free-text input */ });
  }, []);

  const fetchPredictions = useCallback((input: string) => {
    if (!svcRef.current) return;
    setLoading(true);
    svcRef.current.getPlacePredictions(
      { input, componentRestrictions: { country: "ca" }, types: ["address"] },
      (predictions, status) => {
        console.log("Predictions:", predictions, "Status:", status);
        setLoading(false);
        setOptions(predictions ?? []);
        setOpen(true);
      },
    );
  }, []);

  const handleInputChange = (_: React.SyntheticEvent, value: string, reason: string) => {
    if (reason !== "input") return;
    setInputValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 3) { setOptions([]); setOpen(false); return; }
    setLoading(true); // show spinner immediately while debounce ticks
    debounceRef.current = setTimeout(() => fetchPredictions(value), 300);
  };

  const handleSelect = (_: React.SyntheticEvent, value: Prediction | string | null) => {
    if (!value || typeof value === "string" || !detailsRef.current) return;
    setInputValue(value.description);
    setOpen(false);
    detailsRef.current.getDetails(
      { placeId: value.place_id, fields: ["address_components", "formatted_address"] },
      (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.address_components) return;
        const get = (type: string, short = false): string => {
          const c = place.address_components!.find(x => x.types.includes(type));
          return c ? (short ? c.short_name : c.long_name) : "";
        };
        onSelectRef.current({
          streetNumber: get("street_number"),
          streetName:   get("route"),
          city:         get("locality") || get("postal_town") || get("sublocality_level_1"),
          province:     get("administrative_area_level_1", true),
          postalCode:   get("postal_code"),
        });
      },
    );
  };

  // If API key absent or script failed, render a plain text field
  if (!process.env.REACT_APP_GOOGLE_PLACES_API_KEY) {
    return (
      <TextField
        fullWidth
        placeholder="Start typing full address…"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        sx={sx}
        autoComplete="new-address"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><RoomOutlinedIcon /></InputAdornment>
          ),
        }}
      />
    );
  }

  return (
    <MuiAutocomplete<Prediction, false, false, true>
      freeSolo
      open={open}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.description)}
      isOptionEqualToValue={(a, b) => a.place_id === b.place_id}
      filterOptions={(x) => x}
      noOptionsText={apiReady ? "No addresses found" : "Loading…"}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.place_id}
          sx={{ py: "10px !important", px: "14px !important" }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Box sx={{ fontSize: 15, flexShrink: 0, mt: "1px", color: "#94a3b8" }}>
              📍
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", lineHeight: 1.35 }}>
                {option.structured_formatting.main_text}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.4 }}>
                {option.structured_formatting.secondary_text}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Start typing full address…"
          autoComplete="new-address"
          sx={sx}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start"><RoomOutlinedIcon /></InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading && <CircularProgress size={15} sx={{ color: "#94a3b8", mr: 0.5 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

/* ── Design tokens ──────────────────────────────────────────────────── */
const NAVY = "#0d1b2a";
const GOLD = "#c9a84c";
const BORDER = "#e8edf2";
const LABEL_COLOR = "#4a5568";
const BG = "#f4f6f9";

/* ── Reusable field label ────────────────────────────────────────────── */
const FieldLabel = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <Typography
    sx={{
      fontSize: 12, fontWeight: 600, color: LABEL_COLOR,
      mb: 0.6, textTransform: "uppercase", letterSpacing: 0.4,
    }}
  >
    {children}
    {required && <span style={{ color: "#e53935", marginLeft: 3 }}>*</span>}
  </Typography>
);

/* ── Reusable section card ───────────────────────────────────────────── */
const SectionCard = ({
  icon, title, subtitle, children,
}: {
  icon: React.ReactNode; title: string; subtitle?: string; children: React.ReactNode;
}) => (
  <Box
    sx={{
      bgcolor: "#fff", borderRadius: 3, border: `1px solid ${BORDER}`,
      boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflow: "hidden",
    }}
  >
    {/* Section header */}
    <Box
      sx={{
        px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5,
        borderBottom: `1px solid ${BORDER}`, bgcolor: "#fafbfc",
      }}
    >
      <Box
        sx={{
          width: 34, height: 34, borderRadius: 2,
          bgcolor: `${NAVY}10`, display: "flex", alignItems: "center", justifyContent: "center",
          color: NAVY, flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}>{subtitle}</Typography>
        )}
      </Box>
    </Box>
    {/* Section body */}
    <Box sx={{ p: 3 }}>{children}</Box>
  </Box>
);

/* ── Styled text field ───────────────────────────────────────────────── */
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2, fontSize: 14, bgcolor: "#fff",
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: NAVY, borderWidth: 1.5 },
  },
  "& .MuiOutlinedInput-input": { py: "11px", px: "14px" },
  "& .MuiInputAdornment-root .MuiSvgIcon-root": { fontSize: 18, color: "#94a3b8" },
};

/* ═══════════════════════════════════════════════════════════════════════ */

const CreateClient = () => {
  const navigate = useNavigate();
  const [createCustomer, { isLoading }] = useCreateCustomerMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [isImageUploading, setIsImageUploading] = useState(false);
  // Server-side phone error (e.g. duplicate) — shown under the phone field,
  // cleared as soon as the user edits the number.
  const [phoneDupError, setPhoneDupError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      customerFirstName: "",
      customerLastName: "",
      customerMobileNum: "",
      customerEmail: "",
      customerPreferTimeToCall: "",
      customerDescription: "",
      customerStreetNumber: "",
      customerStreetName: "",
      customerCity: "",
      customerProvince: "",
      customerPostalCode: "",
      // kept in payload for API compatibility, but not shown in UI
      customerCompanyName: "",
      customerCompanyPhoneNumebr: "",
      customerCompanyEmail: "",
      customerImg: "",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: Record<string, string> = {};
      const emailErr = validateCustomerEmail(values.customerEmail);
      if (emailErr) errors.customerEmail = emailErr;
      const phoneErr = validateCustomerPhone(values.customerMobileNum);
      if (phoneErr) errors.customerMobileNum = phoneErr;
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        setPhoneDupError(null);
        let customerImg: string | undefined;
        if (selectedImage) {
          setIsImageUploading(true);
          customerImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }
        const payload = { ...values, customerImg };
        const res = await createCustomer(payload).unwrap();
        if (!res.status) {
          // Field-level errors (e.g. duplicate phone) render under the field;
          // the form keeps all entered data so staff can correct and resubmit.
          const phoneErr = res.errors?.customerMobileNum;
          if (typeof phoneErr === "string" && phoneErr) {
            setPhoneDupError(phoneErr);
          } else {
            // Only ever hand the toast a plain string — never the response object.
            showErrorMessage(toErrorMessage(res.message, "Unable to save customer."));
          }
          return;
        }
        // Success — toast first, then land on the new customer's profile page
        showMessage("Customer created successfully");
        resetForm();
        setSelectedImage(undefined);
        const newId = res.data?.id;
        navigate(newId ? `/customer-history/${newId}` : "/customer/list");
      } catch (error) {
        setIsImageUploading(false);
        // Safely stringify — a thrown object must never reach the toast as-is.
        showErrorMessage(toErrorMessage(error));
      }
    },
  });

  const formValid = useMemo(() => {
    const { customerFirstName, customerLastName, customerMobileNum } = formik.values;
    return customerFirstName.trim() !== "" && customerLastName.trim() !== "" && customerMobileNum.trim() !== "";
  }, [formik.values]);

  /* ── Real-time phone validation — single source of truth ─────────── */
  // No excludeId — every match is a real conflict when creating a customer.
  const phoneState = useDuplicatePhoneCheck(formik.values.customerMobileNum);

  /* ── Cross-table awareness ────────────────────────────────────────
   * The hook above ONLY checks the `customers` table. A phone might
   * still be in `lead_threads` (a prior inquiry that never converted
   * to a client). Surface that as a secondary warning so staff don't
   * silently create a duplicate after a missed handoff.
   *
   * Email gets its own check here because the existing field has no
   * duplicate detection at all — the new hook covers it across both
   * the `customers` and `lead_threads` tables.
   */
  const { phoneState: dupCross, emailState: dupEmail } = useLeadDuplicateCheck(
    formik.values.customerMobileNum || "",
    formik.values.customerEmail || "",
  );
  // Phone surfaces ONLY the lead-side match so we don't double up on
  // the customer match that `useDuplicatePhoneCheck` already renders.
  const leadOnlyPhoneMatch = dupCross.match?.type === "lead" ? dupCross.match : null;

  // `phoneDupError` is a separate post-save server error (rare save-race). It
  // is OR-ed into the display but never recomputed — phoneState stays the one
  // source for the real-time validation state.
  const phoneShowsError = phoneState.iconType === "error" || Boolean(phoneDupError);
  const phoneHelperText = phoneState.isDuplicate
    ? undefined // the rich duplicate block below renders the message + link
    : phoneState.errorMessage ||
      phoneDupError ||
      (phoneState.isChecking ? "Checking…" : undefined);

  /* ── Email validation state ─────────────────────────────────────── */
  const emailTouched = formik.touched.customerEmail;
  const emailHasError = Boolean(emailTouched && formik.errors.customerEmail);
  const showEmailSuccess =
    Boolean(emailTouched) &&
    !formik.errors.customerEmail &&
    Boolean((formik.values.customerEmail || "").trim());

  // Reusable end-adornment wrapper — fades a single icon in. Colour is applied
  // via `sx` directly on the icon (never through the parent InputAdornment),
  // so it never inherits the adornment's default grey and renders correctly
  // in both light and dark mode.
  const adornIcon = (icon: React.ReactElement) => (
    <InputAdornment position="end">
      <Fade in timeout={250}>{icon}</Fade>
    </InputAdornment>
  );

  // Phone end adornment — driven entirely by phoneState.iconType.
  const phoneEndAdornment =
    phoneState.iconType === "spinner" ? (
      <InputAdornment position="end">
        <CircularProgress size={18} sx={{ color: "#94a3b8" }} />
      </InputAdornment>
    ) : phoneShowsError ? (
      adornIcon(<ErrorRoundedIcon sx={{ color: "error.main", fontSize: 20 }} />)
    ) : phoneState.iconType === "success" ? (
      adornIcon(<CheckCircleRoundedIcon sx={{ color: "success.main", fontSize: 20 }} />)
    ) : undefined;

  // Email field end adornment — red error → green tick.
  const emailEndAdornment = emailHasError
    ? adornIcon(<ErrorRoundedIcon sx={{ color: "error.main", fontSize: 20 }} />)
    : showEmailSuccess
    ? adornIcon(<CheckCircleRoundedIcon sx={{ color: "success.main", fontSize: 20 }} />)
    : undefined;

  /* ── Image handling ─────────────────────────────────────────────── */
  const resizeImage = (file: File, maxW: number, maxH: number): Promise<File> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          let w = img.width, h = img.height;
          if (w > h ? w > maxW : h > maxH) {
            if (w > h) { h *= maxW / w; w = maxW; } else { w *= maxH / h; h = maxH; }
          }
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          canvas.getContext("2d")?.drawImage(img, 0, 0, w, h);
          canvas.toBlob(
            (blob) => blob ? resolve(new File([blob], file.name, { type: file.type })) : reject(),
            file.type
          );
        };
        img.onerror = reject;
      };
    });

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) { setSelectedImage(undefined); return; }
    const valid = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
    if (!valid.includes(file.type)) { showErrorMessage("Must be jpeg, png, jpg, or gif."); return; }
    if (file.size > 2048 * 1024) { showErrorMessage("File must be smaller than 2 MB."); return; }
    try { setSelectedImage(await resizeImage(file, 500, 500)); }
    catch { showErrorMessage("Failed to process image."); }
  };

  /* ── Address autofill ───────────────────────────────────────────── */
  const handleAddressSelect = useCallback((addr: ParsedAddress) => {
    if (addr.streetNumber) formik.setFieldValue("customerStreetNumber", addr.streetNumber);
    if (addr.streetName)   formik.setFieldValue("customerStreetName",   addr.streetName);
    if (addr.city)         formik.setFieldValue("customerCity",         addr.city);
    if (addr.province)     formik.setFieldValue("customerProvince",     addr.province);
    if (addr.postalCode)   formik.setFieldValue("customerPostalCode",   addr.postalCode);
  }, [formik.setFieldValue]);

  const previewUrl = selectedImage ? URL.createObjectURL(selectedImage) : null;
  const initials = [formik.values.customerFirstName, formik.values.customerLastName]
    .filter(Boolean).map((n) => n[0].toUpperCase()).join("") || "?";

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <Box sx={{ bgcolor: BG, minHeight: "100vh", pb: 10 }}>

      {/* ── Page header ──────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "#fff", borderBottom: `1px solid ${BORDER}`,
          px: { xs: 2, md: 4 }, py: 2.5, mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: NAVY, lineHeight: 1 }}>
          Add New Client
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 0.4 }}>
          Create a new client profile in the system
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 900, mx: "auto" }}>
        {/* Native form submit is disabled — pressing Enter or any default
            browser submit must NOT reload/navigate. Saving happens only via
            the explicit Save button onClick, after async validation runs. */}
        <form onSubmit={(e) => e.preventDefault()} noValidate>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* ── Card 1: Profile Image ─────────────────────────── */}
            <SectionCard
              icon={<CameraAltOutlinedIcon sx={{ fontSize: 18 }} />}
              title="Profile Photo"
              subtitle="Upload a photo to identify this client"
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                {/* Avatar preview */}
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <Avatar
                    src={previewUrl || undefined}
                    sx={{
                      width: 88, height: 88, bgcolor: `${NAVY}15`, color: NAVY,
                      fontSize: 28, fontWeight: 700, border: `2px solid ${BORDER}`,
                    }}
                  >
                    {!previewUrl && initials}
                  </Avatar>
                </Box>

                {/* Upload control */}
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="client-photo-upload"
                    type="file"
                    onChange={handleImageChange}
                  />
                  <label htmlFor="client-photo-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      startIcon={<CameraAltOutlinedIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        textTransform: "none", fontWeight: 600, fontSize: 13,
                        borderColor: BORDER, color: NAVY, borderRadius: 2,
                        "&:hover": { borderColor: NAVY, bgcolor: `${NAVY}05` },
                      }}
                    >
                      {selectedImage ? "Change Photo" : "Upload Photo"}
                    </Button>
                  </label>
                  {selectedImage && (
                    <Button
                      size="small"
                      onClick={() => setSelectedImage(undefined)}
                      sx={{ ml: 1, textTransform: "none", fontSize: 12, color: "#94a3b8" }}
                    >
                      Remove
                    </Button>
                  )}
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.8 }}>
                    JPG, PNG or GIF · Max 2 MB · Recommended 500×500px
                  </Typography>
                </Box>
              </Box>
            </SectionCard>

            {/* ── Card 2: Client Information ────────────────────── */}
            <SectionCard
              icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
              title="Client Information"
              subtitle="Basic personal details"
            >
              <Grid container spacing={2.5}>
                {/* First Name */}
                <Grid item xs={12} sm={6}>
                  <FieldLabel required>First Name</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. John"
                    {...formik.getFieldProps("customerFirstName")}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Last Name */}
                <Grid item xs={12} sm={6}>
                  <FieldLabel required>Last Name</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. Smith"
                    {...formik.getFieldProps("customerLastName")}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Mobile */}
                <Grid item xs={12} sm={6}>
                  <FieldLabel required>Mobile Number</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. 6471234567"
                    {...formik.getFieldProps("customerMobileNum")}
                    onChange={(e) => {
                      if (phoneDupError) setPhoneDupError(null);
                      formik.handleChange(e);
                    }}
                    sx={inputSx}
                    error={phoneShowsError}
                    helperText={phoneHelperText}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneOutlinedIcon />
                        </InputAdornment>
                      ),
                      endAdornment: phoneEndAdornment,
                    }}
                  />
                  {phoneState.duplicateCustomer && (
                    <Box sx={{ mt: 0.6, display: "flex", flexDirection: "column", gap: 0.2 }}>
                      <Typography sx={{ fontSize: 12, color: "#d32f2f", fontWeight: 500, lineHeight: 1.4 }}>
                        {phoneState.duplicateCustomer.customer_name
                          ? <>Phone already belongs to <strong>{phoneState.duplicateCustomer.customer_name}</strong>.</>
                          : "A customer with this phone number already exists."}
                      </Typography>
                      <Link
                        component="button"
                        type="button"
                        onClick={() => navigate(`/customer-history/${phoneState.duplicateCustomer!.customer_id}`)}
                        sx={{ fontSize: 12, fontWeight: 600, textAlign: "left", width: "fit-content" }}
                      >
                        View Customer Profile →
                      </Link>
                    </Box>
                  )}
                  {/* Lead-side duplicate awareness — only renders when
                      the customer check above came up empty but the
                      phone IS attached to a lead_threads row. The
                      customer-side warning above stays as-is so this
                      page never double-warns about the same record. */}
                  {!phoneState.duplicateCustomer && (
                    <DuplicateClientCard match={leadOnlyPhoneMatch} dense />
                  )}
                </Grid>

                {/* Email */}
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Email Address</FieldLabel>
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="e.g. john@email.com"
                    {...formik.getFieldProps("customerEmail")}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      // Trim + lowercase automatically when the user leaves the field.
                      const cleaned = e.target.value.trim().toLowerCase();
                      if (cleaned !== e.target.value) {
                        formik.setFieldValue("customerEmail", cleaned);
                      }
                    }}
                    sx={inputSx}
                    error={emailHasError || !!dupEmail.match}
                    helperText={emailHasError ? formik.errors.customerEmail : undefined}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon />
                        </InputAdornment>
                      ),
                      endAdornment: emailEndAdornment,
                    }}
                  />
                  <DuplicateClientCard match={dupEmail.match} dense />
                </Grid>

                {/* Preferred Time to Call */}
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Preferred Time to Call</FieldLabel>
                  <TextField
                    select
                    fullWidth
                    {...formik.getFieldProps("customerPreferTimeToCall")}
                    sx={inputSx}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (value) =>
                        value ? (
                          (value as string)
                        ) : (
                          <span style={{ color: "#94a3b8" }}>
                            Select preferred callback time
                          </span>
                        ),
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTimeOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {preferredCallTimes.map((slot) => (
                      <MenuItem key={slot} value={slot}>
                        {slot}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Spacer on large screens */}
                <Grid item xs={12} sm={6} sx={{ display: { xs: "none", sm: "block" } }} />

                {/* Notes — full width */}
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2, borderColor: BORDER }} />
                  <FieldLabel>Notes</FieldLabel>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Any additional notes about this client…"
                    {...formik.getFieldProps("customerDescription")}
                    sx={{
                      ...inputSx,
                      "& .MuiOutlinedInput-input": { py: "12px", px: "14px" },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: "12px" }}>
                          <NoteAltOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── Card 3: Address ───────────────────────────────── */}
            <SectionCard
              icon={<HomeOutlinedIcon sx={{ fontSize: 18 }} />}
              title="Address"
              subtitle="Client's residential or mailing address"
            >
              <Grid container spacing={2.5}>

                {/* ── Client Address — autocomplete ────────────────── */}
                <Grid item xs={12}>
                  <FieldLabel>Client Address</FieldLabel>
                  <AddressAutocomplete onSelect={handleAddressSelect} inputSx={inputSx} />
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.6, lineHeight: 1.4 }}>
                    Select an address to auto-fill the fields below
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ borderColor: BORDER }} />
                </Grid>

                {/* Street No */}
                <Grid item xs={12} sm={4}>
                  <FieldLabel>Street No.</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. 120"
                    {...formik.getFieldProps("customerStreetNumber")}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HomeOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Street Name */}
                <Grid item xs={12} sm={8}>
                  <FieldLabel>Street Name</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. King Street West"
                    {...formik.getFieldProps("customerStreetName")}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HomeOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* City */}
                <Grid item xs={12} sm={4}>
                  <FieldLabel>City</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. Toronto"
                    {...formik.getFieldProps("customerCity")}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationCityOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Province */}
                <Grid item xs={12} sm={4}>
                  <FieldLabel>Province</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. Ontario"
                    {...formik.getFieldProps("customerProvince")}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationCityOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Postal Code */}
                <Grid item xs={12} sm={4}>
                  <FieldLabel>Postal Code</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. M5H 2N2"
                    {...formik.getFieldProps("customerPostalCode")}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MarkunreadMailboxOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </SectionCard>

          </Box>
        </form>
      </Box>

      {/* ── Fixed action footer ───────────────────────────────────── */}
      <Box
        sx={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
          bgcolor: "#fff", borderTop: `1px solid ${BORDER}`,
          boxShadow: "0 -2px 12px rgba(0,0,0,0.06)",
          px: { xs: 2, md: 4 }, py: 1.8,
          display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5,
        }}
      >
        <Button
          type="button"
          variant="outlined"
          startIcon={<CloseOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => { formik.resetForm(); setSelectedImage(undefined); navigate("/customer/list"); }}
          sx={{
            textTransform: "none", fontWeight: 600, fontSize: 13,
            borderColor: BORDER, color: "#64748b", borderRadius: 2, px: 3,
            "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          startIcon={<SaveOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => formik.handleSubmit()}
          disabled={
            !formValid ||
            isLoading ||
            isImageUploading ||
            phoneState.isChecking ||
            phoneState.isDuplicate ||
            phoneState.isFormatInvalid ||
            phoneState.isIncomplete
          }
          sx={{
            textTransform: "none", fontWeight: 700, fontSize: 13,
            bgcolor: NAVY, color: "#fff", borderRadius: 2, px: 3.5,
            boxShadow: "none",
            "&:hover": { bgcolor: "#1e3a5f", boxShadow: "none" },
            "&:disabled": { bgcolor: "#cbd5e1", color: "#fff" },
          }}
        >
          {isLoading || isImageUploading ? "Saving…" : "Save Client"}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateClient;
